import { useQuery } from '@zg-rentals/react-query5';
import { format } from 'date-fns';
import { useDispatch } from 'react-redux';

import { merge } from '@zg-rentals/zdash';

import ErrorRMW from '../../model/ErrorRMW';
import ListingCompsModel from '../../model/ListingComps';
import ListingViewStatisticsModel from '../../model/ListingViewStatistics';
import { RENTAL_GROWTH_GRAPHQL_API_V1_PREFIX } from '../../constants/RouteConstants';
import makeRequest from '../../utils/makeRequestUtils';
import useRequestConfig from '../useRequestConfig';
import Constants from '../../constants';

import { growthGraphQLClient } from '../../utils/requesters';
import { getCompsByListingAliasQuery, getCompStatsByListingAliasQuery } from '../../utils/graphqlUtils';
import { logError } from '@zg-rentals/log-error';

const formatDate = (timestamp) => {
  if (!timestamp) {
    return '';
  }

  const dateToFormat = new Date(timestamp * 1000);
  return format(
    new Date(dateToFormat.getUTCFullYear(), dateToFormat.getUTCMonth(), dateToFormat.getUTCDate()),
    'MM/dd/yy',
  );
};

const useFetchCompStats = (listingAlias) => {
  const query = getCompStatsByListingAliasQuery(listingAlias);

  const dispatch = useDispatch();
  const requestConfig = useRequestConfig(Constants.POST);

  const queryFn = () =>
    makeRequest({
      dispatch,
      endpoint: `${RENTAL_GROWTH_GRAPHQL_API_V1_PREFIX}/listing`,
      requestConfig,
      config: { data: query },
      request: growthGraphQLClient,
    });

  return useQuery({
    queryKey: ['comparableStats', listingAlias],
    queryFn: async () => {
      try {
        const {
          data: { data, errors },
        } = await queryFn();

        if (errors?.length > 0) {
          const error = errors[0];
          const { code, message } = error;

          // some errors are ok
          const isAcceptableErrorCode =
            code === 'RENT_ZESTIMATE_NOT_FOUND' || code === 'PROPERTY_DETAILS_NOT_FOUND' || code === 'INVALID_INPUT';

          // if error is not acceptable
          if (isAcceptableErrorCode === false) {
            // log response error (not user fiendly)
            logError({
              error,
              context: {
                message: `code: ${code}; message: ${message}`,
                details: 'fetchCompStats',
                moreInfo: 'errors[0] is what is logged, see `context.allErrors` for the rest',
                allErrors: errors,
              },
            });

            // display user friendly message
            throw ErrorRMW({ message: 'No information found for this listing.' });
          }
        }

        if (
          !data ||
          !data.hasOwnProperty('byListingAlias') ||
          (data?.byListingAlias?.similarFloorplans &&
            Object.keys(data?.byListingAlias?.similarFloorplans).length === 0) ||
          !data?.byListingAlias?.similarFloorplans?.stats?.startDate
        ) {
          throw ErrorRMW({ message: 'No information found for this listing.' });
        }

        const formattedResponse = merge(ListingViewStatisticsModel({}), data.byListingAlias);

        // format dates as String
        formattedResponse.similarFloorplans.stats.startDate = formatDate(
          formattedResponse.similarFloorplans.stats.startDate,
        );
        formattedResponse.similarFloorplans.stats.endDate = formatDate(
          formattedResponse.similarFloorplans.stats.endDate,
        );
        formattedResponse.stats.startDate = formatDate(formattedResponse.stats.startDate);
        formattedResponse.stats.endDate = formatDate(formattedResponse.stats.endDate);

        return formattedResponse;
      } catch (error) {
        if (error.message !== 'No information found for this listing.') {
          logError({
            error,
            context: 'fetchCompStats',
          });
        }
        throw error;
      }
    },
    enabled: Boolean(listingAlias),
    staleTime: Infinity,
  });
};

const useFetchComparables = (listingAlias) => {
  const query = getCompsByListingAliasQuery(listingAlias);

  const dispatch = useDispatch();
  const requestConfig = useRequestConfig(Constants.POST);

  const queryFn = () =>
    makeRequest({
      dispatch,
      endpoint: `${RENTAL_GROWTH_GRAPHQL_API_V1_PREFIX}/listing`,
      requestConfig,
      config: { data: query },
      request: growthGraphQLClient,
    });

  return useQuery({
    queryKey: ['comparables', listingAlias],
    queryFn: async () => {
      try {
        const {
          data: { data, errors },
        } = await queryFn();

        if (errors?.length > 0) {
          const error = errors[0];
          const { code, message } = error;
          // some errors are ok
          const isAcceptableErrorCode = code === 'RENT_ZESTIMATE_NOT_FOUND' || 'PROPERTY_DETAILS_NOT_FOUND';

          // if error is not acceptable
          if (isAcceptableErrorCode === false) {
            throw ErrorRMW({ message });
          }
        }

        // no response
        if (!data || !data.hasOwnProperty('byListingAlias')) {
          throw ErrorRMW({ message: 'No information found for this listing.' });
        }

        return ListingCompsModel(data.byListingAlias);
      } catch (error) {
        logError({
          error,
          context: 'useFetchComparables',
        });
        throw error;
      }
    },
    enabled: Boolean(listingAlias),
    staleTime: Infinity,
  });
};

export { useFetchCompStats, useFetchComparables };
